import {commonControllers} from '@mintdata-wsp/common/controllers/CommonControllers';
import {rootState} from '@mintdata-wsp/common/state/RootState';
import {IFontProps} from '@mintdata/mint-kit/api/assets/IAssetsProps';
import {Font} from '@mintdata/mint-kit/api/models/Font';
import {
    FontCategory,
    FontItem,
    FontName,
    FontType,
    GoogleFontItem,
    IFontsState,
    SystemFonts
} from '@mintdata/mint-kit/api/models/FontItem';
import {FontVariant, FontWeight} from '@mintdata/mint-kit/api/models/FontWeight';
import {SystemFont} from '@mintdata/mint-kit/api/models/TypeFace';
import {AString} from '@mintdata/mint-kit/api/values/AString';
import bind from 'bind-decorator';
import {action, observable, reaction} from 'mobx';

const GOOGLE_FONTS_API_KEY = 'AIzaSyAwTD3Ijn4I0FHbdqzbjKeVmLEa_M-UENo';

export class FontsController
{
    public constructor()
    {
    }

    public getProps():IFontProps
    {
        return {
            getState: this._getState,
            loadFont: this.loadFont,
            unloadFont: this.unloadFont,
            checkAndLoadFont: this.checkAndLoadFont,
            addFontToFavorites: this.addFontToFavorites,
            removeFontFromFavorites: this.removeFontFromFavorites,
            removeFontFromRecent: this.removeFontFromRecent,
            isExternalFontsLoaded: this._isExternalFontsLoaded,
        };
    }

    private _getState():IFontsState
    {
        const openDesign = commonControllers.designState.getOpenDesign();

        return openDesign && openDesign.designState ? openDesign.designState.assets.fontsState : null;
    }

    private _isDesignMode:boolean = false;

    @observable
    private _externalFontsLoaded:boolean = false;

    public init()
    {
        reaction(() =>
            {
                return rootState.ui.visualDesigner.openDesign;
            },
            () =>
            {
                const openDesign = commonControllers.designState.getOpenDesign();

                if( !openDesign )
                {
                    return;
                }

                const {isPublishMode} = openDesign;

                this._isDesignMode = !isPublishMode;

                this._loadUsedFonts();

                if( this._isDesignMode )
                    this._loadGoogleFonts();
            }
        );
    }

    @bind
    private _isExternalFontsLoaded():Promise<void>
    {
        return new Promise<void>((resolve, reject) =>
        {
            if( this._externalFontsLoaded )
            {
                resolve();
            }
            else
            {
                reaction(() =>
                    {
                        return this._externalFontsLoaded;
                    },
                    () =>
                    {
                        if( this._externalFontsLoaded )
                        {
                            resolve();
                        }
                    }
                );
            }
        });
    }

    @action.bound
    public addFontToFavorites(fontName:FontName):void
    {
        const fontsState = this._getState();

        if( !fontsState ) return;

        fontsState.favoriteFonts.set(fontName, true);
    }

    @action.bound
    public removeFontFromFavorites(fontName:FontName):void
    {
        const fontsState = this._getState();

        if( !fontsState ) return;

        fontsState.favoriteFonts.delete(fontName);
    }

    @action.bound
    public removeFontFromRecent(fontName:FontName):void
    {
        const fontsState = this._getState();

        if( !fontsState ) return;

        fontsState.recentFonts.delete(fontName);
    }

    @bind
    public checkAndLoadFont(newFont:Font, oldFont?:Font):void
    {
        if( !newFont ) return;

        const newFontName = newFont.typeface.toString();

        if( !oldFont )
        {
            this.loadFont(newFontName);
        }
        else
        {
            const oldFontName = oldFont.typeface.toString();

            if( newFontName !== oldFontName )
            {
                this.loadFont(newFontName, true, true);
                this._removeFromUsedFonts(oldFontName);
            }
        }
    }

    @action.bound
    private _addToUsedFonts(fontName:FontName)
    {
        const fontsState = this._getState();

        if( !fontsState ) return;

        const {usedFonts, recentFonts} = fontsState;

        const oldValue = usedFonts.get(fontName) || 0;

        usedFonts.set(fontName, oldValue + 1);
        recentFonts.set(fontName, true);
    }

    @action.bound
    private _removeFromUsedFonts(fontName:FontName)
    {
        const fontsState = this._getState();

        if( !fontsState ) return;

        const {usedFonts} = fontsState;

        let oldValue = usedFonts.get(fontName);

        if( !oldValue ) return;

        const newValue = oldValue - 1;

        if( !newValue )
            usedFonts.delete(fontName);
        else
            usedFonts.set(fontName, newValue);
    }

    @action.bound
    public unloadFont(font:Font,):void
    {
        this._removeFromUsedFonts(font.typeface.toString());
    }

    @action.bound
    public loadFont(fontName:FontName, addToUsedFonts = true, forceLoad = false):void
    {
        const fontsState = this._getState();

        if( !fontsState ) return;

        const {usedFonts, loadedFonts} = fontsState;

        const isLoaded = loadedFonts.has(fontName);

        if( isLoaded && !addToUsedFonts && !forceLoad )
        {
            return;
        }

        const isUsedFont:boolean = !!usedFonts.get(fontName);

        if( addToUsedFonts )
        {
            this._addToUsedFonts(fontName);
        }

        if( !isLoaded || forceLoad )
        {
            if( addToUsedFonts || usedFonts.has(fontName) )
            {
                this._loadFont(fontName);
            }
            else
            {
                // TODO: !!! .REGULAR ???
                this._loadFont(fontName, FontVariant.REGULAR);
            }

            loadedFonts.set(fontName, true);
        }
        else
        {
            // if( isLoaded )
            //     console.log('%c isLoaded 2 =', 'background:#f00;color:#ff0;', fontName);
        }
    }

    private _loadFont(fontName:FontName, fontVariant?:FontVariant)
    {
        const fontsState = this._getState();

        if( !fontsState ) return;

        const {systemFonts, externalFonts, usedFonts} = fontsState;

        let fontItem:FontItem = systemFonts.get(fontName);

        if( fontItem && fontItem.type !== FontType.EXTERNAL_SYSTEM )
        {
            return;
        }

        if( !fontItem && externalFonts ) fontItem = externalFonts.get(fontName);

        if( !fontItem && this._isDesignMode && !usedFonts.has(fontName) ) return;

        const apiUrl = [];

        // https://developers.google.com/fonts/docs/getting_started

        apiUrl.push('https://fonts.googleapis.com/css?family=');
        apiUrl.push(fontName.replace(/ /g, '+'));

        if( fontVariant && fontItem && fontItem.variants.has(fontVariant) )
        {
            apiUrl.push(':');
            apiUrl.push(FontWeight.toGoogleFontVariantParam(fontVariant));

            // TODO: ?
            // apiUrl.push('&subset=');
            // apiUrl.push('latin');
        }
        else if( fontItem && fontItem.variants.size )
        {
            apiUrl.push(':');

            fontItem.variants.forEach((isEnabled:boolean, fontVariant:FontVariant) =>
            {
                if( !isEnabled ) return;

                apiUrl.push(FontWeight.toGoogleFontVariantParam(fontVariant));
                apiUrl.push(',');
            });

            apiUrl.pop(); // remove last , (comma)
        }
        else
        {
            // load all variants
            apiUrl.push(':');

            FontWeight.types.forEach((fontVariant:AString) =>
            {
                apiUrl.push(FontWeight.toGoogleFontVariantParam(fontVariant.value as FontVariant));
                apiUrl.push(',');
            });

            apiUrl.pop(); // remove last , (comma)
        }

        // https://www.w3.org/TR/css-fonts-4/#font-display-desc
        apiUrl.push('&display=swap');
        // apiUrl.push('&display=block');
        // apiUrl.push('&display=auto');

        var url = apiUrl.join('');

        const headID = document.getElementsByTagName('head')[0];
        const newLink = document.createElement('link');
        newLink.type = 'text/css';
        newLink.rel = 'stylesheet';
        headID.appendChild(newLink);

        newLink.href = url;
    }

    @action
    private _loadGoogleFonts()
    {
        const fontsState = this._getState();

        if( !fontsState || fontsState.externalFonts.size !== 0 ) return;

        const {systemFonts, recentFonts} = fontsState;

        const url = `https://www.googleapis.com/webfonts/v1/webfonts?key=${GOOGLE_FONTS_API_KEY}`;

        // fontsState.externalFonts = new Map();

        fetch(url, {
            method: 'get',
            headers: new Headers({'Accept': 'application/json'})
        })
            .then(response => response.json())
            .then(action((googleFontsList:any) =>
            {
                googleFontsList.items.forEach((font:GoogleFontItem, index:number) =>
                {
                    const fontName:FontName = font.family;

                    const externalFontItem = new FontItem(
                        fontName,
                        FontType.EXTERNAL,
                        font.category,
                        font.variants
                    );

                    fontsState.externalFonts.set(fontName, externalFontItem);

                    // if( usedFonts.has(fontName) || recentFonts.has(fontName) || favoriteFonts.has(fontName) || systemFonts.has(fontName) )

                    // (-) usedFonts -> already loaded
                    // (-) favoriteFonts -> need only in dialog
                    // (+) recentFonts & systemFonts -> used in property panel font settings
                    if( recentFonts.has(fontName) || systemFonts.has(fontName) )
                    {
                        this.loadFont(fontName, false, false);
                    }
                });

                this._externalFontsLoaded = true;
            }));
    }

    @action
    private _loadUsedFonts()
    {
        const fontsState = this._getState();

        if( !fontsState ) return;

        const {usedFonts, systemFonts} = fontsState;

        Array.from(usedFonts.keys()).forEach((fontName:FontName) =>
        {
            const isSystemFont = systemFonts.has(fontName);

            let needLoad = false;

            if( !isSystemFont )
            {
                needLoad = true;
            }
            else
            {
                const fontItem:FontItem = systemFonts.get(fontName);

                if( fontItem && fontItem.type === FontType.EXTERNAL_SYSTEM )
                    needLoad = true;
            }

            if( needLoad )
                this.loadFont(fontName, false, true);
        });
    }

    public static getSystemFonts():SystemFonts
    {
        const defaultFontVariants = [
            FontVariant.REGULAR,
            FontVariant.ITALIC,
            FontVariant.BOLD,
            FontVariant.BOLD_ITALIC,
        ];

        const systemFonts:SystemFonts = new Map();

        systemFonts.set(
            SystemFont.ARIAL,
            new FontItem(SystemFont.ARIAL, FontType.SYSTEM, FontCategory.SANS_SERIF, defaultFontVariants)
        );

        systemFonts.set(
            SystemFont.TAHOMA,
            new FontItem(SystemFont.TAHOMA, FontType.SYSTEM, FontCategory.SANS_SERIF, defaultFontVariants)
        );

        systemFonts.set(
            SystemFont.GEORGIA,
            new FontItem(SystemFont.GEORGIA, FontType.SYSTEM, FontCategory.SERIF, defaultFontVariants)
        );

        systemFonts.set(
            SystemFont.COURIER_NEW,
            new FontItem(SystemFont.COURIER_NEW, FontType.SYSTEM, FontCategory.SERIF, defaultFontVariants)
        );

        systemFonts.set(
            SystemFont.TREBUCHET_MS,
            new FontItem(SystemFont.TREBUCHET_MS, FontType.SYSTEM, FontCategory.SANS_SERIF, defaultFontVariants)
        );

        systemFonts.set(
            SystemFont.COMIC_SANS_MS,
            new FontItem(SystemFont.COMIC_SANS_MS, FontType.SYSTEM, FontCategory.SANS_SERIF, defaultFontVariants)
        );

        systemFonts.set(
            SystemFont.VERDANA,
            new FontItem(SystemFont.VERDANA, FontType.SYSTEM, FontCategory.SANS_SERIF, defaultFontVariants)
        );

        systemFonts.set(
            SystemFont.TIMES_NEW_ROMAN,
            new FontItem(SystemFont.TIMES_NEW_ROMAN, FontType.SYSTEM, FontCategory.SERIF, defaultFontVariants)
        );

        systemFonts.set(
            SystemFont.HELVETICA_NEUE,
            new FontItem(SystemFont.HELVETICA_NEUE, FontType.SYSTEM, FontCategory.SANS_SERIF, [
                FontVariant.THIN,
                FontVariant.THIN_ITALIC,
                FontVariant.EXTRA_LIGHT,
                FontVariant.EXTRA_LIGHT_ITALIC,
                // FontVariant.LIGHT,
                // FontVariant.LIGHT_ITALIC,
                FontVariant.REGULAR,
                FontVariant.ITALIC,
                FontVariant.MEDIUM,
                FontVariant.MEDIUM_ITALIC,
                FontVariant.SEMI_BOLD,
                FontVariant.SEMI_BOLD_ITALIC,
                // FontVariant.BOLD,
                // FontVariant.BOLD_ITALIC,
                // FontVariant.EXTRA_BOLD,
                // FontVariant.EXTRA_BOLD_ITALIC,
                // FontVariant.BLACK,
                // FontVariant.BLACK_ITALIC,
            ])
        );

        systemFonts.set(
            SystemFont.CALIBRI,
            new FontItem(SystemFont.CALIBRI, FontType.SYSTEM, FontCategory.SANS_SERIF, [
                // FontVariant.THIN,
                // FontVariant.THIN_ITALIC,
                // FontVariant.EXTRA_LIGHT,
                // FontVariant.EXTRA_LIGHT_ITALIC,
                // FontVariant.LIGHT,
                // FontVariant.LIGHT_ITALIC,
                FontVariant.REGULAR,
                FontVariant.ITALIC,
                // FontVariant.MEDIUM,
                // FontVariant.MEDIUM_ITALIC,
                // FontVariant.SEMI_BOLD,
                // FontVariant.SEMI_BOLD_ITALIC,
                FontVariant.BOLD,
                FontVariant.BOLD_ITALIC,
                // FontVariant.EXTRA_BOLD,
                // FontVariant.EXTRA_BOLD_ITALIC,
                // FontVariant.BLACK,
                // FontVariant.BLACK_ITALIC,
            ])
        );

        systemFonts.set(
            SystemFont.ROBOTO,
            new FontItem(SystemFont.ROBOTO, FontType.EXTERNAL_SYSTEM, FontCategory.SANS_SERIF, [
                FontVariant.THIN,
                FontVariant.THIN_ITALIC,
                // FontVariant.EXTRA_LIGHT,
                // FontVariant.EXTRA_LIGHT_ITALIC,
                FontVariant.LIGHT,
                FontVariant.LIGHT_ITALIC,
                FontVariant.REGULAR,
                FontVariant.ITALIC,
                FontVariant.MEDIUM,
                FontVariant.MEDIUM_ITALIC,
                // FontVariant.SEMI_BOLD,
                // FontVariant.SEMI_BOLD_ITALIC,
                FontVariant.BOLD,
                FontVariant.BOLD_ITALIC,
                // FontVariant.EXTRA_BOLD,
                // FontVariant.EXTRA_BOLD_ITALIC,
                FontVariant.BLACK,
                FontVariant.BLACK_ITALIC,
            ])
        );

        systemFonts.set(
            SystemFont.ROBOTO_MONO,
            new FontItem(SystemFont.ROBOTO_MONO, FontType.EXTERNAL_SYSTEM, FontCategory.MONOSPACE, [
                FontVariant.THIN,
                FontVariant.THIN_ITALIC,
                // FontVariant.EXTRA_LIGHT,
                // FontVariant.EXTRA_LIGHT_ITALIC,
                FontVariant.LIGHT,
                FontVariant.LIGHT_ITALIC,
                FontVariant.REGULAR,
                FontVariant.ITALIC,
                FontVariant.MEDIUM,
                FontVariant.MEDIUM_ITALIC,
                // FontVariant.SEMI_BOLD,
                // FontVariant.SEMI_BOLD_ITALIC,
                FontVariant.BOLD,
                FontVariant.BOLD_ITALIC,
                // FontVariant.EXTRA_BOLD,
                // FontVariant.EXTRA_BOLD_ITALIC,
                // FontVariant.BLACK,
                // FontVariant.BLACK_ITALIC,
            ])
        );

        systemFonts.set(
            SystemFont.ROBOTO_CONDENSED,
            new FontItem(SystemFont.ROBOTO_CONDENSED, FontType.EXTERNAL_SYSTEM, FontCategory.SANS_SERIF, [
                // FontVariant.THIN,
                // FontVariant.THIN_ITALIC,
                // FontVariant.EXTRA_LIGHT,
                // FontVariant.EXTRA_LIGHT_ITALIC,
                FontVariant.LIGHT,
                FontVariant.LIGHT_ITALIC,
                FontVariant.REGULAR,
                FontVariant.ITALIC,
                // FontVariant.MEDIUM,
                // FontVariant.MEDIUM_ITALIC,
                // FontVariant.SEMI_BOLD,
                // FontVariant.SEMI_BOLD_ITALIC,
                FontVariant.BOLD,
                FontVariant.BOLD_ITALIC,
                // FontVariant.EXTRA_BOLD,
                // FontVariant.EXTRA_BOLD_ITALIC,
                // FontVariant.BLACK,
                // FontVariant.BLACK_ITALIC,
            ])
        );

        systemFonts.set(
            SystemFont.MONTSERRAT,
            new FontItem(SystemFont.MONTSERRAT, FontType.EXTERNAL_SYSTEM, FontCategory.SANS_SERIF, [
                FontVariant.THIN,
                FontVariant.THIN_ITALIC,
                FontVariant.EXTRA_LIGHT,
                FontVariant.EXTRA_LIGHT_ITALIC,
                FontVariant.LIGHT,
                FontVariant.LIGHT_ITALIC,
                FontVariant.REGULAR,
                FontVariant.ITALIC,
                FontVariant.MEDIUM,
                FontVariant.MEDIUM_ITALIC,
                FontVariant.SEMI_BOLD,
                FontVariant.SEMI_BOLD_ITALIC,
                FontVariant.BOLD,
                FontVariant.BOLD_ITALIC,
                FontVariant.EXTRA_BOLD,
                FontVariant.EXTRA_BOLD_ITALIC,
                FontVariant.BLACK,
                FontVariant.BLACK_ITALIC,
            ])
        );

        systemFonts.set(
            SystemFont.OPEN_SANS,
            new FontItem(SystemFont.OPEN_SANS, FontType.EXTERNAL_SYSTEM, FontCategory.SANS_SERIF, [
                // FontVariant.THIN,
                // FontVariant.THIN_ITALIC,
                // FontVariant.EXTRA_LIGHT,
                // FontVariant.EXTRA_LIGHT_ITALIC,
                FontVariant.LIGHT,
                FontVariant.LIGHT_ITALIC,
                FontVariant.REGULAR,
                FontVariant.ITALIC,
                // FontVariant.MEDIUM,
                // FontVariant.MEDIUM_ITALIC,
                FontVariant.SEMI_BOLD,
                FontVariant.SEMI_BOLD_ITALIC,
                FontVariant.BOLD,
                FontVariant.BOLD_ITALIC,
                FontVariant.EXTRA_BOLD,
                FontVariant.EXTRA_BOLD_ITALIC,
                // FontVariant.BLACK,
                // FontVariant.BLACK_ITALIC,
            ])
        );

        systemFonts.set(
            SystemFont.OPEN_SANS_CONDENSED,
            new FontItem(SystemFont.OPEN_SANS_CONDENSED, FontType.EXTERNAL_SYSTEM, FontCategory.SANS_SERIF, [
                // FontVariant.THIN,
                // FontVariant.THIN_ITALIC,
                // FontVariant.EXTRA_LIGHT,
                // FontVariant.EXTRA_LIGHT_ITALIC,
                FontVariant.LIGHT,
                FontVariant.LIGHT_ITALIC,
                // FontVariant.REGULAR,
                // FontVariant.ITALIC,
                // FontVariant.MEDIUM,
                // FontVariant.MEDIUM_ITALIC,
                // FontVariant.SEMI_BOLD,
                // FontVariant.SEMI_BOLD_ITALIC,
                FontVariant.BOLD,
                // FontVariant.BOLD_ITALIC,
                // FontVariant.EXTRA_BOLD,
                // FontVariant.EXTRA_BOLD_ITALIC,
                // FontVariant.BLACK,
                // FontVariant.BLACK_ITALIC,
            ])
        );

        return systemFonts;
    }
}
